import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, Loader2, Save, LayoutTemplate } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { DOC_CATEGORIES, RIGHT_DEFS, CADENCES } from "@/lib/rights";

export const RightsEditorDialog = ({ open, onOpenChange, companyId, investor, onSaved }) => {
  const [rights, setRights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiText, setAiText] = useState("");
  const [docs, setDocs] = useState([]);
  const [sourceDoc, setSourceDoc] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [templates, setTemplates] = useState({ presets: [], custom: [] });

  useEffect(() => {
    if (!open || !investor) return;
    setLoading(true);
    api.get(`/companies/${companyId}/investors/${investor.id}/rights`)
      .then(({ data }) => setRights(data)).finally(() => setLoading(false));
    api.get(`/companies/${companyId}/documents`).then(({ data }) => setDocs(data)).catch(() => {});
    api.get(`/companies/${companyId}/rights-templates`).then(({ data }) => setTemplates(data)).catch(() => {});
  }, [open, investor, companyId]);

  const setField = (k, v) => setRights((r) => ({ ...r, [k]: v }));
  const toggleCat = (c) => setRights((r) => ({ ...r, allowed_categories: r.allowed_categories.includes(c) ? r.allowed_categories.filter((x) => x !== c) : [...r.allowed_categories, c] }));
  const toggleRight = (k, v) => setRights((r) => ({ ...r, rights: { ...r.rights, [k]: v } }));

  const allTemplates = [...(templates.presets || []), ...(templates.custom || [])];
  const applyTemplate = (id) => {
    const tpl = allTemplates.find((t) => t.id === id);
    if (!tpl) return;
    const p = tpl.rights_payload || {};
    setRights((r) => ({
      ...r,
      tier: p.tier ?? r.tier,
      reporting_cadence: p.reporting_cadence ?? r.reporting_cadence,
      allowed_categories: [...(p.allowed_categories || [])],
      rights: { ...(p.rights || {}) },
      notes: p.notes ?? r.notes,
      source: "template",
    }));
    toast.success(`Applied "${tpl.name}" template. Review & save.`);
  };

  const extract = async () => {
    if (!aiText.trim() && !sourceDoc) return toast.error("Paste agreement text or pick a document");
    setExtracting(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/ai/extract-rights`, { raw_text: aiText, document_id: sourceDoc || null });
      setRights((r) => ({ ...data, updated_at: r?.updated_at }));
      toast.success("AI extracted rights from the agreement. Review & save.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Extraction failed"); }
    finally { setExtracting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/investors/${investor.id}/rights`, rights);
      toast.success("Information rights saved");
      onOpenChange(false); onSaved && onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Information Rights - {investor?.name}</DialogTitle></DialogHeader>
        {loading || !rights ? <p className="text-sm text-muted-foreground">Loading...</p> : (
          <div className="space-y-5">
            <div className="rounded-xl border bg-secondary/30 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><LayoutTemplate className="h-4 w-4 text-primary" /> Apply a rights template</div>
              <Select value="" onValueChange={applyTemplate}>
                <SelectTrigger className="mt-1" data-testid="rights-apply-template"><SelectValue placeholder="Choose a template to prefill…" /></SelectTrigger>
                <SelectContent>
                  {(templates.presets || []).length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Built-in presets</SelectLabel>
                      {templates.presets.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`rights-template-opt-${t.id}`}>{t.name}</SelectItem>)}
                    </SelectGroup>
                  )}
                  {(templates.custom || []).length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Your templates</SelectLabel>
                      {templates.custom.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`rights-template-opt-${t.id}`}>{t.name}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-muted-foreground">Prefills the fields below - you can still edit before saving.</p>
            </div>

            <div className="rounded-xl border border-dashed bg-secondary/40 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Extract from investment agreement (AI)</div>
              {docs.length > 0 && (
                <div className="mb-2">
                  <Label className="text-xs">Use an uploaded document</Label>
                  <Select value={sourceDoc} onValueChange={setSourceDoc}>
                    <SelectTrigger className="mt-1" data-testid="rights-source-doc"><SelectValue placeholder="Optional: choose a document" /></SelectTrigger>
                    <SelectContent>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.category})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <Textarea value={aiText} onChange={(e) => setAiText(e.target.value)} placeholder="...or paste the information-rights clause / agreement text here" className="min-h-[90px]" data-testid="rights-ai-text" />
              <Button size="sm" onClick={extract} disabled={extracting} className="mt-2 gap-1.5" data-testid="rights-ai-extract">
                {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Extract rights
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label>Tier / class</Label><Input value={rights.tier} onChange={(e) => setField("tier", e.target.value)} className="mt-1" data-testid="rights-tier" /></div>
              <div><Label>Reporting cadence</Label>
                <Select value={rights.reporting_cadence} onValueChange={(v) => setField("reporting_cadence", v)}>
                  <SelectTrigger className="mt-1" data-testid="rights-cadence"><SelectValue /></SelectTrigger>
                  <SelectContent>{CADENCES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Documents this investor may request</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                {DOC_CATEGORIES.map((c) => (
                  <label key={c} className="flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-1.5 text-sm hover:bg-secondary">
                    <Checkbox checked={rights.allowed_categories.includes(c)} onCheckedChange={() => toggleCat(c)} data-testid={`rights-cat-${c}`} />{c}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Specific rights</Label>
              <div className="mt-2 space-y-1.5">
                {RIGHT_DEFS.map((rd) => (
                  <div key={rd.k} className="flex items-center justify-between rounded-lg border px-3 py-1.5">
                    <span className="text-sm">{rd.label}</span>
                    <Switch checked={!!rights.rights?.[rd.k]} onCheckedChange={(v) => toggleRight(rd.k, v)} data-testid={`rights-flag-${rd.k}`} />
                  </div>
                ))}
              </div>
            </div>

            <div><Label>Notes (shown to investor)</Label><Textarea value={rights.notes} onChange={(e) => setField("notes", e.target.value)} className="mt-1" data-testid="rights-notes" /></div>
          {rights.source === "ai_extracted" && <Badge data-testid="rights-ai-extracted" className="border-brand/30 bg-brand/10 text-brand-ink">AI-extracted - please verify</Badge>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !rights} className="gap-2" data-testid="rights-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rights</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
